/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useAuth } from "auth/Auth";
import { useModuleEntitlements } from "hooks/useModuleEntitlements";

const STATUS_COLOR = {
  APPROVED: "success",
  PENDING: "warning",
  REJECTED: "error",
  PAUSED: "warning",
  DISABLED: "default",
};

/** Meta locale codes → readable labels (value sent to Meta stays the code). */
const LANGUAGE_OPTIONS = [
  { value: "en_US", label: "English (US)" },
  { value: "en_GB", label: "English (UK)" },
  { value: "en", label: "English" },
  { value: "hi", label: "Hindi" },
  { value: "es", label: "Spanish" },
  { value: "es_ES", label: "Spanish (Spain)" },
  { value: "es_MX", label: "Spanish (Mexico)" },
  { value: "pt_BR", label: "Portuguese (Brazil)" },
  { value: "pt_PT", label: "Portuguese (Portugal)" },
  { value: "fr", label: "French" },
  { value: "fr_FR", label: "French (France)" },
  { value: "de", label: "German" },
  { value: "it", label: "Italian" },
  { value: "ar", label: "Arabic" },
  { value: "id", label: "Indonesian" },
  { value: "ms", label: "Malay" },
  { value: "zh_CN", label: "Chinese (Simplified)" },
  { value: "zh_TW", label: "Chinese (Traditional)" },
  { value: "ja", label: "Japanese" },
  { value: "ko", label: "Korean" },
  { value: "ru", label: "Russian" },
  { value: "tr", label: "Turkish" },
  { value: "nl", label: "Dutch" },
  { value: "pl", label: "Polish" },
  { value: "th", label: "Thai" },
  { value: "vi", label: "Vietnamese" },
  { value: "bn", label: "Bengali" },
  { value: "ta", label: "Tamil" },
  { value: "te", label: "Telugu" },
  { value: "mr", label: "Marathi" },
  { value: "gu", label: "Gujarati" },
  { value: "kn", label: "Kannada" },
  { value: "ml", label: "Malayalam" },
  { value: "pa", label: "Punjabi" },
];

const LANGUAGE_LABELS = Object.fromEntries(
  LANGUAGE_OPTIONS.map(({ value, label }) => [value, label])
);

export function formatWhatsAppLanguage(code) {
  if (!code) return "";
  return LANGUAGE_LABELS[code] || code.replace(/_/g, "-");
}

const EMPTY_FORM = {
  name: "",
  language: "en_US",
  body: "",
  header_text: "",
  footer_text: "",
  category: "UTILITY",
};

function errorDetail(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || JSON.stringify(item)).join(" ");
  }
  return error?.message || "Something went wrong.";
}

export default function WhatsAppTemplatesSettings() {
  const { axiosInstance } = useAuth();
  const { isModuleEnabled, loading: entitlementsLoading } = useModuleEntitlements();
  const whatsappEnabled = !entitlementsLoading && isModuleEnabled("WHATSAPP");

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/whatsapp/templates", {
        params: { include_inactive: true },
      });
      setTemplates(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setToast({ severity: "error", message: errorDetail(error) });
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => {
    if (whatsappEnabled) loadTemplates();
    else setLoading(false);
  }, [whatsappEnabled, loadTemplates]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axiosInstance.post("/whatsapp/templates/sync");
      setToast({
        severity: "success",
        message: res.data?.message || "Templates synced from Meta.",
      });
      await loadTemplates();
    } catch (error) {
      setToast({ severity: "error", message: errorDetail(error) });
    } finally {
      setSyncing(false);
    }
  };

  const handleCreate = async () => {
    const name = form.name.trim().toLowerCase();
    const body = form.body.trim();
    if (!name || !body) return;
    setSaving(true);
    try {
      await axiosInstance.post("/whatsapp/templates", {
        name,
        language: form.language.trim() || "en_US",
        body,
        header_text: form.header_text.trim() || null,
        footer_text: form.footer_text.trim() || null,
        category: form.category.trim() || null,
        status: "PENDING",
        is_active: false,
      });
      setToast({
        severity: "success",
        message: `Template “${name}” added as pending. Sync from Meta once it is approved before agents can send it.`,
      });
      setOpenCreate(false);
      setForm(EMPTY_FORM);
      await loadTemplates();
    } catch (error) {
      setToast({ severity: "error", message: errorDetail(error) });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (template) => {
    try {
      await axiosInstance.put(`/whatsapp/templates/${template.id}`, {
        is_active: !template.is_active,
      });
      setTemplates((prev) =>
        prev.map((row) =>
          row.id === template.id ? { ...row, is_active: !row.is_active } : row
        )
      );
    } catch (error) {
      setToast({ severity: "error", message: errorDetail(error) });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`/whatsapp/templates/${deleteTarget.id}`);
      setToast({
        severity: "success",
        message: `Removed “${deleteTarget.name}” from PearlCRM. It still exists in Meta.`,
      });
      setDeleteTarget(null);
      await loadTemplates();
    } catch (error) {
      setToast({ severity: "error", message: errorDetail(error) });
    } finally {
      setDeleting(false);
    }
  };

  if (entitlementsLoading) {
    return (
      <MDBox display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </MDBox>
    );
  }

  if (!whatsappEnabled) {
    return (
      <Alert severity="info">
        WhatsApp is not enabled for this tenant. Turn on the WhatsApp module under
        entitlements, then connect it in Settings → Integrations → Social.
      </Alert>
    );
  }

  return (
    <>
      <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} mb={2} flexWrap="wrap">
        <Box>
          <MDTypography variant="h6" fontWeight="medium" sx={{ fontSize: "1rem" }}>
            WhatsApp Templates
          </MDTypography>
          <Typography variant="body2" color="text.secondary" mt={0.5} maxWidth={560}>
            Templates must be created and approved in Meta Business Manager. Sync
            them here, or add the exact Meta name so agents can send them from
            Messages after the 24-hour window.
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexShrink={0}>
          <Button
            variant="outlined"
            color="info"
            startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
            onClick={handleSync}
            disabled={syncing}
            sx={{
              textTransform: "none",
              // Theme outlined buttons inherit white text from contained styles —
              // force readable ink on the white surface.
              color: "info.main",
              borderColor: "info.main",
              backgroundColor: "transparent",
              "&:hover": {
                borderColor: "info.dark",
                backgroundColor: "rgba(11, 11, 12, 0.06)",
                color: "info.main",
              },
              "&.Mui-disabled": {
                color: "text.disabled !important",
                borderColor: "divider",
              },
            }}
          >
            Sync from Meta
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setForm(EMPTY_FORM);
              setOpenCreate(true);
            }}
            sx={{ backgroundColor: "info.main", color: "white !important", textTransform: "none" }}
          >
            Add template
          </Button>
        </Box>
      </MDBox>

      {loading ? (
        <MDBox display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </MDBox>
      ) : templates.length === 0 ? (
        <MDBox textAlign="center" py={8}>
          <MDTypography variant="h6" color="text.secondary" mb={1}>
            No WhatsApp templates yet
          </MDTypography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Create the template in Meta, wait until it is Approved, then Sync from
            Meta — or add it here using the same name Meta assigned.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenCreate(true)}
            sx={{ backgroundColor: "info.main", color: "white !important" }}
          >
            Add template
          </Button>
        </MDBox>
      ) : (
        <Box display="flex" flexDirection="column" gap={1.5}>
          {templates.map((template) => (
            <Box
              key={template.id}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "10px",
                p: 2,
                bgcolor: template.is_active ? "white" : "#fafafa",
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                <Box minWidth={0} flex={1}>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.75}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {template.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={template.status}
                      color={STATUS_COLOR[template.status] || "default"}
                      sx={{ height: 22 }}
                    />
                    {template.language ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={formatWhatsAppLanguage(template.language)}
                        sx={{ height: 22 }}
                      />
                    ) : null}
                    {template.category ? (
                      <Chip size="small" variant="outlined" label={template.category} sx={{ height: 22 }} />
                    ) : null}
                    {template.variable_count > 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        {template.variable_count} variable{template.variable_count === 1 ? "" : "s"}
                      </Typography>
                    ) : null}
                  </Box>
                  {template.header_text ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Header: {template.header_text}
                    </Typography>
                  ) : null}
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", mt: 0.5 }}
                  >
                    {template.body || "—"}
                  </Typography>
                  {template.footer_text ? (
                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                      {template.footer_text}
                    </Typography>
                  ) : null}
                </Box>
                <Box display="flex" alignItems="center" flexShrink={0}>
                  <Tooltip title={template.is_active ? "Enabled for agents" : "Hidden from agents"}>
                    <Switch
                      size="small"
                      checked={Boolean(template.is_active)}
                      onChange={() => handleToggleActive(template)}
                    />
                  </Tooltip>
                  <Tooltip title="Remove from PearlCRM">
                    <IconButton size="small" color="error" onClick={() => setDeleteTarget(template)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Dialog open={openCreate} onClose={() => !saving && setOpenCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add WhatsApp template</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            The name must match the template Meta approved (lowercase letters,
            digits and underscores only). Placeholders use Meta’s format: {"{{1}}"},
            {" {{2}}"}.
          </DialogContentText>
          <TextField
            fullWidth
            required
            label="Template name"
            placeholder="order_update"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            sx={{ mb: 2, mt: 0.5 }}
          />
          <Box display="flex" gap={2} mb={2}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="wa-template-language-label">Language</InputLabel>
              <Select
                labelId="wa-template-language-label"
                label="Language"
                value={form.language}
                onChange={(e) => setForm({ ...form, language: e.target.value })}
              >
                {LANGUAGE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="wa-template-category-label">Category</InputLabel>
              <Select
                labelId="wa-template-category-label"
                label="Category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                <MenuItem value="UTILITY">Utility</MenuItem>
                <MenuItem value="MARKETING">Marketing</MenuItem>
                <MenuItem value="AUTHENTICATION">Authentication</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <TextField
            fullWidth
            label="Header (optional)"
            value={form.header_text}
            onChange={(e) => setForm({ ...form, header_text: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            required
            multiline
            minRows={4}
            label="Body"
            placeholder="Hi {{1}}, your order {{2}} is on the way."
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Footer (optional)"
            value={form.footer_text}
            onChange={(e) => setForm({ ...form, footer_text: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={saving || !form.name.trim() || !form.body.trim()}
            sx={{ color: "white !important" }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>Remove template</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove “{deleteTarget?.name}” from PearlCRM? This does not delete it in
            Meta — agents just will not see it until you add or sync it again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={20} color="inherit" /> : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : null}
      </Snackbar>
    </>
  );
}
